// react
import { useMemo } from 'react';

// components
import { ArticleCard } from '@/components/atoms/ArticleCard/ArticleCard';
import { CardContainer } from '@/components/templates/CardContainer/CardContainer';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';

// store
import { Article } from '@karehero/models';

// hooks/
import { useWindowSize } from '@/hooks/';

// theme
import theme from '@/theme';

// mui
import { Box, Typography } from '@mui/material';

/**
 * ArticleBookmarks shows a list of bookmarked articles.
 */
export const ArticleBookmarks = ({
  articles,
  isLoading,
  onBookmark,
  onArticleClick,
  onBack,
}: ArticleBookmarksProps) => {
  // hooks/
  const [width] = useWindowSize();

  // memos
  const maxColumns = useMemo(() => {
    if (width < theme.breakpoint.mdValue) return 1;
    if (width < theme.breakpoint.lgValue) return 2;
    if (width < theme.breakpoint.xlValue) return 3;
    return 4;
  }, [width]);

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={<Typography variant='h5'>My Bookmarks</Typography>}
        contentSlot={
          <>
            {articles?.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 100,
                  color: 'text.secondary',
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                }}
              >
                <Typography variant='body1'>No bookmarks yet</Typography>
              </Box>
            )}
            <CardContainer maxColumns={maxColumns}>
              {articles?.map((article, i) => (
                <ArticleCard
                  key={i}
                  article={article}
                  onBookmark={onBookmark}
                  onClick={onArticleClick}
                  isLoading={isLoading}
                  isBookmarked
                />
              ))}
            </CardContainer>
          </>
        }
      />
    </ModalPageTemplate>
  );
};

interface ArticleBookmarksProps {
  className?: string;
  articles?: Partial<Article>[];
  isLoading: boolean;
  onBookmark?: (article: Partial<Article>) => void;
  onArticleClick?: (article?: Partial<Article>) => void;
  onBack?: () => void;
}

ArticleBookmarks.displayName = 'ArticleBookmarks';

export default ArticleBookmarks;
